import { useSearchParams, Link } from '@remix-run/react'
import {
	LuArrowLeft,
	LuArrowLeftToLine,
	LuArrowRight,
	LuArrowRightToLine,
} from 'react-icons/lu'
import { Button } from './ui/button'

function setSearchParamsString(
	searchParams: URLSearchParams,
	changes: Record<string, string | number | undefined>,
) {
	const newSearchParams = new URLSearchParams(searchParams)

	for (const [key, value] of Object.entries(changes)) {
		if (value === undefined) {
			newSearchParams.delete(key)
			continue
		}

		newSearchParams.set(key, String(value))
	}

	return Array.from(newSearchParams.entries())
		.map(([key, value]) =>
			value ? `${key}=${encodeURIComponent(value)}` : key,
		)
		.join('&')
}

export const PaginationBar = ({ total }: { total: number }) => {
	const [searchParams] = useSearchParams()
	const $skip = Number(searchParams.get('$skip')) || 0
	const $top = Number(searchParams.get('$top')) || 10

	const totalPages = Math.ceil(total / $top)
	const currentPage = Math.floor($skip / $top) + 1
	const maxPages = 5
	const halfMaxPages = Math.floor(maxPages / 2)

	const canPageBackwards = $skip > 0
	const canPageForwards = $skip + $top < total

	const pageNumbers = [] as Array<number>
	if (totalPages <= maxPages) {
		for (let i = 1; i <= totalPages; i++) {
			pageNumbers.push(i)
		}
	} else {
		let startPage = currentPage - halfMaxPages
		let endPage = currentPage + halfMaxPages

		if (startPage < 1) {
			endPage += Math.abs(startPage) + 1
			startPage = 1
		}

		if (endPage > totalPages) {
			startPage -= endPage - totalPages
			endPage = totalPages
		}

		for (let i = startPage; i <= endPage; i++) {
			pageNumbers.push(i)
		}
	}

	return (
		<div className="mx-auto mt-8 flex max-w-7xl flex-col items-center px-1 pb-6 lg:row-start-2 lg:row-end-2 lg:px-4 xl:w-11/12 2xl:w-10/12">
			<div className="flex items-center gap-1">
				<Button disabled={!canPageBackwards}>
					<Link
						to={{ search: setSearchParamsString(searchParams, { $skip: 0 }) }}
						preventScrollReset
						prefetch="intent"
					>
						<span className="sr-only">First page</span>
						<LuArrowLeftToLine size={'24px'} />
					</Link>
				</Button>
				<Button disabled={!canPageBackwards}>
					<Link
						to={{
							search: setSearchParamsString(searchParams, {
								$skip: Math.max($skip - $top, 0),
							}),
						}}
						preventScrollReset
						prefetch="intent"
					>
						<span className="sr-only">Previous page</span>
						<LuArrowLeft size={'24px'} />
					</Link>
				</Button>

				{pageNumbers.map(pageNumber => {
					const pageSkip = (pageNumber - 1) * $top
					const isCurrentPage = pageNumber === currentPage

					if (isCurrentPage) {
						return (
							<Button key={`${pageNumber}-active`}>
								<div>
									<span className="sr-only">Page {pageNumber}</span>
									<span>{pageNumber}</span>
								</div>
							</Button>
						)
					} else {
						return (
							<Button key={pageNumber}>
								<Link
									to={{
										search: setSearchParamsString(searchParams, {
											$skip: pageSkip,
										}),
									}}
								>
									{pageNumber}
								</Link>
							</Button>
						)
					}
				})}

				<Button disabled={!canPageForwards}>
					<Link
						to={{
							search: setSearchParamsString(searchParams, {
								$skip: $skip + $top,
							}),
						}}
						preventScrollReset
						prefetch="intent"
					>
						<span className="sr-only">Next page</span>
						<LuArrowRight size={'24px'} />
					</Link>
				</Button>
				<Button disabled={!canPageForwards}>
					<Link
						to={{
							search: setSearchParamsString(searchParams, {
								$skip: (totalPages - 1) * $top,
							}),
						}}
						preventScrollReset
						prefetch="intent"
					>
						<span className="sr-only">Last page</span>
						<LuArrowRightToLine size={'24px'} />
					</Link>
				</Button>
			</div>
		</div>
	)
}
